import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import { notify } from '../../notifications';
import { apiError } from '../Documents/format';
import RunBanner from './components/RunBanner';
import TimesheetSettingsDialog from './components/TimesheetSettingsDialog';
import TimesheetSummaryBar from './components/TimesheetSummaryBar';
import TimesheetTable from './components/TimesheetTable';
import TimesheetToolbar from './components/TimesheetToolbar';
import { hasAnyFilter } from './constants';
import { useTimesheetList } from './hooks/useTimesheetList';
import { useTimesheetRun } from './hooks/useTimesheetRun';
import { useTimesheetSummary } from './hooks/useTimesheetSummary';
import { isCancelled, timesheetApi } from './timesheetApi';
import { TimesheetMeta, TimesheetRow } from './types';
import { fmtDay } from './utils';

interface MetaError { text: string; forbidden: boolean }

/** Ссылки табелей ведут только в Google Таблицы: любой другой адрес от сервера не открываем */
const DOCS_URL = /^https:\/\/docs\.google\.com\//;

/**
 * «Табель РБ»: аптеки зоны оператора со статусом табеля, переход в Google-таблицу, а также (по праву на настройки)
 * папка и год, создание недостающих табелей, сверка с папкой и история запусков.
 * Каждый блок рендерится только при наличии соответствующего права.
 */
const TabelRB: React.FC = () => {
  const [meta, setMeta] = useState<TimesheetMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busyNum, setBusyNum] = useState<string | null>(null);

  const canData = Boolean(meta && meta.access.data);
  const canSettings = Boolean(meta && meta.access.settings);

  const list = useTimesheetList(canData);
  const summary = useTimesheetSummary(canData);
  const { reload: reloadList } = list;
  const { reload: reloadSummary } = summary;

  const loadMeta = useCallback((signal?: AbortSignal) => {
    setMetaError(null);
    return timesheetApi.meta(signal)
      .then(res => setMeta(res))
      .catch(err => {
        if (isCancelled(err)) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить раздел «Табель РБ»'), forbidden: err?.response?.status === 403 });
      });
  }, []);

  /** Тихое обновление доступов, папки и года: ошибка не заменяет экран раздела */
  const refreshMeta = useCallback(() => {
    timesheetApi.meta().then(res => setMeta(res)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const reloadAll = useCallback(() => {
    refreshMeta();
    reloadSummary();
    reloadList();
  }, [refreshMeta, reloadSummary, reloadList]);

  const runCtl = useTimesheetRun(canSettings, reloadAll);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  /** year не указан - табель текущего года; указан - табель прошлого года (пока он доступен) */
  const handleOpen = useCallback(async (row: TimesheetRow, year?: number) => {
    // окно открывается сразу по клику, иначе браузер заблокирует его как всплывающее после ожидания ответа
    const win = window.open('', '_blank');
    setBusyNum(row.pharmacyNum);
    try {
      const { url } = await timesheetApi.openFile(row.pharmacyNum, year);
      if (!DOCS_URL.test(url)) throw new Error('bad url');
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (err: any) {
      if (win) win.close();
      notify.error(apiError(err, 'Не удалось открыть табель'));
    } finally {
      setBusyNum(null);
    }
  }, []);

  const handleCreate = useCallback(async (row: TimesheetRow) => {
    setBusyNum(row.pharmacyNum);
    try {
      const res = await timesheetApi.createFile(row.pharmacyNum);
      notify.success(res.status === 'exists' ? `Табель уже был создан: ${res.fileName}` : `Создан табель: ${res.fileName}`);
      reloadSummary();
      reloadList();
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось создать табель'));
    } finally {
      setBusyNum(null);
    }
  }, [reloadSummary, reloadList]);

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity={metaError.forbidden ? 'warning' : 'error'}
          action={metaError.forbidden ? undefined : <Button color="inherit" size="small" onClick={() => { loadMeta(); }}>Повторить</Button>}
        >
          {metaError.forbidden ? 'У вас нет доступа к разделу «Табель РБ». Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const { access } = meta;
  const runActive = canSettings && runCtl.running;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      {!meta.folderConfigured && (
        <Alert
          severity="info"
          action={access.settings ? <Button color="inherit" size="small" onClick={openSettings}>Настройки</Button> : undefined}
        >
          {access.settings
            ? 'Папка Google Drive для табелей не настроена. Укажите её в настройках.'
            : 'Папка для табелей ещё не настроена. Обратитесь к администратору.'}
        </Alert>
      )}

      {runActive && runCtl.run && <RunBanner run={runCtl.run} />}

      {access.data && (
        <>
          <TimesheetSummaryBar summary={summary.summary} error={summary.error} onRetry={reloadSummary} />

          <TimesheetToolbar
            filters={list.filters} loading={list.loading} canSettings={access.settings}
            onSearch={list.setSearch} onStatus={list.setStatus} onReset={list.resetFilters}
            onRefresh={reloadAll} onSettings={openSettings}
          />

          {meta.prev && access.open && (
            <Alert severity="info">
              До {fmtDay(meta.prev.until)} доступны табели {meta.prev.year} года: рядом с табелем {meta.year} года у аптек появилась вторая кнопка.
            </Alert>
          )}

          <TimesheetTable
            rows={list.rows} pagination={list.pagination} loading={list.loading} error={list.error} sort={list.sort}
            hasFilters={hasAnyFilter(list.filters)} folderConfigured={meta.folderConfigured} access={access}
            year={meta.year} prev={meta.prev} runActive={runActive} busyNum={busyNum}
            onSort={list.toggleSort} onPage={list.setPage} onLimit={list.setLimit}
            onOpen={handleOpen} onCreate={handleCreate} onResetFilters={list.resetFilters} onRetry={reloadList}
          />
        </>
      )}

      {/* Нет права на вывод данных: список не показывается, остаётся только то, что разрешено */}
      {!access.data && access.settings && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">Управление папкой, годом и созданием табелей</Typography>
          <Button variant="contained" size="small" startIcon={<SettingsIcon />} onClick={openSettings} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
            Настройки
          </Button>
        </Paper>
      )}

      {!access.data && !access.settings && (
        <Alert severity="info">В этом разделе для вас пока нет доступных действий. Обратитесь к администратору.</Alert>
      )}

      {access.settings && (
        <TimesheetSettingsDialog
          open={settingsOpen} folderConfigured={meta.folderConfigured} canCycle={access.cycle} runCtl={runCtl}
          onClose={closeSettings} onChanged={reloadAll}
        />
      )}
    </Box>
  );
};

export default TabelRB;
